// @ts-check
import { useMemo } from "react";

import { Loader } from "@transfr-inc/dashboard-components";
import {
  buildRegistry,
  MetricRegistryProvider,
  OutcomesEmptyState,
  OutcomesEmptyStateKind,
} from "@transfr-inc/dashboard-components/outcome-metrics";

import { useMetricCatalog } from "@queries/outcomes";

/**
 * Builds the metric registry from the data service's catalog and publishes it to
 * the outcomes surface.
 *
 * A metric's name, the grains it can be read at, and the series it splits into
 * come from the catalog, so an unreadable or malformed one renders the
 * unavailable state for the whole surface rather than a guessed vocabulary. The
 * contract failure `buildRegistry` throws is caught here rather than left to an
 * error boundary, which would take down the app shell around this surface.
 */
export function MetricRegistryGate({ children }) {
  const { data, isLoading, isError, refetch } = useMetricCatalog();

  const registry = useMemo(() => {
    if (!data) {
      return;
    }
    try {
      const built = buildRegistry(data);
      // A catalog naming none of the metrics the app can present leaves every
      // consumer with no metric to select, so it reads as unavailable too.
      return built.metrics.length > 0 ? built : undefined;
    } catch {
      return;
    }
  }, [data]);

  if (isLoading) {
    return <Loader fixed />;
  }

  if (isError || !registry) {
    return (
      <OutcomesEmptyState
        kind={OutcomesEmptyStateKind.UNAVAILABLE}
        level="page"
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <MetricRegistryProvider registry={registry}>
      {children}
    </MetricRegistryProvider>
  );
}

export default MetricRegistryGate;
